import {useCallback, useEffect, useState} from "react";
import type {KeptUpload} from "core/upload/resume/keptUploads";

/**
 * The thin React side of the unfinished uploads this browser kept (kitecorp/transferify-ui#3204): it asks
 * `resumeRuntime` which kept upload to offer the signed-in account, and whether one of theirs was just ended past
 * saving, and holds the answer. What is offered and what is past saving are the runtime's rules; this only
 * re-asks when told to. With nobody signed in it still asks, so the runtime forgets what is past saving for
 * whoever left it, but nothing is offered; while the session is still being restored it asks nothing, because
 * until then a signed-in sender would read as nobody, and their own uploads past saving must be ended on the api
 * by them, not merely forgotten here.
 *
 * The runtime is imported on demand, inside the effect: the composer renders for every landing visitor, and
 * none of it belongs in the eager landing bundle.
 */
export function useResumableUpload(ownerUid: string | null | undefined): {
	offer: KeptUpload | null;
	/** One of the account's unfinished uploads was just ended past saving, unsent: say so once. */
	expired: boolean;
	refresh: () => void;
	acknowledgeExpired: () => void;
} {
	const [offer, setOffer] = useState<KeptUpload | null>(null);
	const [expired, setExpired] = useState(false);
	const [revision, setRevision] = useState(0);
	useEffect(() => {
		if (ownerUid === undefined) {
			return;
		}
		let current = true;
		void import("core/upload/resume/resumeRuntime")
			.then(runtime => runtime.keptUploadsFor(ownerUid))
			// A browser that cannot read its storage (a private window, storage blocked) simply has nothing to offer.
			.catch(() => ({offer: null, expired: false}))
			.then(kept => {
				if (current) {
					setOffer(kept.offer);
					setExpired(previous => previous || kept.expired);
				}
			});
		return () => {
			current = false;
		};
	}, [ownerUid, revision]);
	const refresh = useCallback(() => setRevision(previous => previous + 1), []);
	const acknowledgeExpired = useCallback(() => setExpired(false), []);
	return {offer, expired, refresh, acknowledgeExpired};
}
